/**
 * Auto Gravage
 * - Configure GRAVAGE_VARIANT_ID with the service variant ID (numeric).
 * - Metafield trigger: product.metafields.custom.require_gravage (Boolean).
 * - data-require-gravage is injected on the product add-to-cart form in blocks/buy-buttons.liquid.
 */
(() => {
  if (window.__autoGravageInitialized) return;
  window.__autoGravageInitialized = true;

  /**
   * @typedef {object} CartItem
   * @property {string} key
   * @property {number} quantity
   * @property {number} variant_id
   * @property {Record<string, string> | undefined} [properties]
   */

  /**
   * @typedef {object} Cart
   * @property {number} item_count
   * @property {CartItem[]} items
   */

  // Numeric variant ID for the "Gravage" service.
  const GRAVAGE_VARIANT_ID = 57195351540037;
  // Mêmes IDs que dans ecotaxe.js
  const ECOTAXE_VARIANT_IDS = [57673999974725, 57674490642757];
  const SERVICE_VARIANT_IDS = new Set([GRAVAGE_VARIANT_ID, ...ECOTAXE_VARIANT_IDS]);
  const GRAVAGE_PROPERTY = '_isGravage';

  const REQUIRE_GRAVAGE_ATTRIBUTE = 'data-require-gravage';
  const REQUIRE_GRAVAGE_PROPERTY = '_requireGravage';
  const CART_UPDATE_EVENT = 'cart:update';
  const ADD_ENDPOINT = '/cart/add.js';
  const CART_ENDPOINT = '/cart.js';
  const CHANGE_ENDPOINT = '/cart/change.js';

  const SELECTOR_FORM = 'form[action*="/cart/add"]';
  const SYNC_DEBOUNCE_MS = 200;

  const IS_CART_PAGE = window.location.pathname.startsWith('/cart');

  // Clients PRO (custom.professionnel = true) : pas de BICYCODE
  const IS_PRO = document.body?.dataset?.isPro === 'true';

  let isSyncing = false;
  /** @type {ReturnType<typeof window.setTimeout> | null} */
  let syncTimer = null;
  let warnedMissingVariantId = false;

  /** @param {unknown} value */
  const parseBoolean = (value) => value === true || value === 'true' || value === '1';

  /**
   * @param {FormData} formData
   * @param {HTMLFormElement | null} form
   */
  const getQuantity = (formData, form) => {
    const rawEntry = formData.get('quantity');
    const raw = typeof rawEntry === 'string' ? rawEntry : null;
    const productFormComponent = /** @type {HTMLElement | null} */ (
      form?.closest('product-form-component')
    );
    const fallback = productFormComponent?.dataset?.quantityDefault;
    const quantity = parseInt(raw || fallback || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  /** @param {FormData} formData */
  const addLineItemProperty = (formData) => {
    const key = `properties[${REQUIRE_GRAVAGE_PROPERTY}]`;
    if (!formData.has(key)) {
      formData.set(key, '1');
    }
  };

  /** @param {Element | null} element */
  const ensureHiddenPropertyInput = (element) => {
    const form = element instanceof HTMLFormElement ? element : null;
    if (!form || !form.matches(SELECTOR_FORM)) return;

    const requireGravage = parseBoolean(form.getAttribute(REQUIRE_GRAVAGE_ATTRIBUTE));
    if (!requireGravage) return;

    const name = `properties[${REQUIRE_GRAVAGE_PROPERTY}]`;
    if (form.querySelector(`input[name="${name}"]`)) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = '1';
    form.appendChild(input);
  };

  /**
   * @param {Cart} cart
   * @param {string} [source]
   */
  const dispatchCartUpdate = (cart, source = 'auto-gravage') => {
    const itemCount = cart?.item_count ?? 0;
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: source,
          data: { source, itemCount },
        },
      })
    );
  };

  /** @returns {Promise<Cart>} */
  const fetchCart = async () => {
    const response = await fetch(CART_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  };

  /**
   * @param {BodyInit} body
   * @param {boolean} [isJson]
   */
  const addItem = async (body, isJson = false) => {
    /** @type {Record<string, string>} */
    const headers = { Accept: 'application/json' };
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(ADD_ENDPOINT, {
      method: 'POST',
      headers,
      body,
    });

    const data = await response.json();
    if (!response.ok || data?.status) {
      throw new Error(data?.message || 'Failed to add to cart');
    }

    return data;
  };

  /** @param {number} quantity */
  const addGravage = async (quantity) => {
    if (GRAVAGE_VARIANT_ID <= 0 || quantity <= 0) return null;

    return addItem(
      JSON.stringify({
        items: [
          {
            id: GRAVAGE_VARIANT_ID,
            quantity,
            properties: {
              [GRAVAGE_PROPERTY]: '1',
            },
          },
        ],
      }),
      true
    );
  };

  /**
   * @param {string} lineItemKey
   * @param {number} quantity
   * @returns {Promise<Cart>}
   */
  const changeLineItemQuantity = async (lineItemKey, quantity) => {
    const response = await fetch(CHANGE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error('Failed to change cart item');
    return data;
  };

  /** @param {Cart} cart */
  /** @returns {Set<string> | null} */
  const getDomRequiredLineItemKeys = () => {
    if (!IS_CART_PAGE) return null;
    const keys = new Set();
    document
      .querySelectorAll('.cart-items__table-row[data-key][data-require-gravage="true"]')
      .forEach((row) => {
        const key = row.getAttribute('data-key');
        if (key) keys.add(key);
      });
    return keys;
  };

  /**
   * @param {Cart} cart
   * @param {Set<string> | null} [domRequiredKeys]
   */
  const countRequiredQuantity = (cart, domRequiredKeys = null) => {
    if (!cart?.items) return 0;
    return cart.items.reduce((total, item) => {
      if (SERVICE_VARIANT_IDS.has(Number(item.variant_id))) return total;
      const requires = item?.properties?.[REQUIRE_GRAVAGE_PROPERTY];
      if (!requires) {
        if (domRequiredKeys && item?.key && domRequiredKeys.has(item.key)) {
          return total + (item.quantity || 0);
        }
        return total;
      }
      return total + (item.quantity || 0);
    }, 0);
  };

  /** @param {CartItem} item */
  const isCleanGravageItem = (item) =>
    Object.keys(item.properties || {}).every((key) => key === GRAVAGE_PROPERTY);

  /** @param {Cart} cart */
  const findGravageItems = (cart) =>
    cart?.items?.filter((item) => Number(item.variant_id) === Number(GRAVAGE_VARIANT_ID)) ?? [];

  /** @param {{ source?: string }} [options] */
  const syncGravageFromCart = async (options = {}) => {
    if (isSyncing || GRAVAGE_VARIANT_ID <= 0) return;
    isSyncing = true;

    try {
      const cart = await fetchCart();
      const gravageItems = findGravageItems(cart);
      let updatedCart = cart;

      // Clients PRO : retirer le gravage s'il est dans le panier
      if (IS_PRO) {
        for (const item of gravageItems) {
          updatedCart = await changeLineItemQuantity(item.key, 0);
        }
        if (gravageItems.length) dispatchCartUpdate(updatedCart, options.source || 'auto-gravage');
        return;
      }

      // Ligne gravage qui a reçu les propriétés d'un vélo : on la remplace par une ligne propre
      for (const item of gravageItems.filter((item) => !isCleanGravageItem(item))) {
        updatedCart = await changeLineItemQuantity(item.key, 0);
      }
      const gravageItem = gravageItems.find(isCleanGravageItem);

      const domRequiredKeys = getDomRequiredLineItemKeys();
      const requiredQuantity = countRequiredQuantity(cart, domRequiredKeys);

      if (requiredQuantity <= 0) {
        if (gravageItem) {
          updatedCart = await changeLineItemQuantity(gravageItem.key, 0);
        }
      } else if (gravageItem) {
        if (gravageItem.quantity !== requiredQuantity) {
          updatedCart = await changeLineItemQuantity(gravageItem.key, requiredQuantity);
        }
      } else {
        await addGravage(requiredQuantity);
        updatedCart = await fetchCart();
      }

      dispatchCartUpdate(updatedCart, options.source || 'auto-gravage');
    } catch (error) {
      console.error('[auto-gravage] sync failed', error);
    } finally {
      isSyncing = false;
    }
  };

  const scheduleSync = () => {
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncGravageFromCart({ source: 'auto-gravage' });
    }, SYNC_DEBOUNCE_MS);
  };

  /** @param {SubmitEvent} event */
  const handleSubmit = async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !form.matches(SELECTOR_FORM)) return;

    // Ensure the cart line item carries the marker property even if some other script
    // ends up doing a native submit/redirect.
    ensureHiddenPropertyInput(form);

    const requireGravage = parseBoolean(form.getAttribute(REQUIRE_GRAVAGE_ATTRIBUTE));
    if (!requireGravage || IS_PRO) return;

    if (GRAVAGE_VARIANT_ID <= 0) {
      if (!warnedMissingVariantId) {
        console.warn('[auto-gravage] Missing GRAVAGE_VARIANT_ID, skipping auto add.');
        warnedMissingVariantId = true;
      }
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const formData = new FormData(form);
    const quantity = getQuantity(formData, form);
    addLineItemProperty(formData);

    // Gravage avant le vélo : PPLR réécrit ensuite la ligne n°1 du panier,
    // qui doit être le vélo et non le gravage.
    try {
      await addGravage(quantity);
    } catch (error) {
      console.error('[auto-gravage] add gravage failed', error);
    }

    try {
      await addItem(formData);
    } catch (error) {
      console.error('[auto-gravage] add main item failed, falling back to native submit', error);
      form.submit();
      return;
    }

    await syncGravageFromCart({ source: 'auto-gravage' });
  };

  /** @param {Event} event */
  const handleCartUpdate = (event) => {
    if (event instanceof CustomEvent && event?.detail?.data?.source === 'auto-gravage') return;
    scheduleSync();
  };

  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);

  // Inject the hidden property on all matching forms (and any added later), so a
  // normal form POST (redirect to /cart) still allows sync on the cart page.
  const bootstrapHiddenInputs = () => {
    document.querySelectorAll(SELECTOR_FORM).forEach(ensureHiddenPropertyInput);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapHiddenInputs, { once: true });
  } else {
    bootstrapHiddenInputs();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(SELECTOR_FORM)) {
          ensureHiddenPropertyInput(node);
        }
        node.querySelectorAll?.(SELECTOR_FORM).forEach(ensureHiddenPropertyInput);
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (IS_CART_PAGE) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
    } else {
      scheduleSync();
    }
  }
})();
